import React, { useState, useEffect } from 'react';
import { Col, Input, Form, Radio, Button } from '../../../../components';
import {
    useManageAccountSendMFAPhoneNumberCodeMutation,
    useManageAccountVerifyMFAPhoneNumberCodeMutation,
} from '../../../../api/auth';
import { useNotificationContext } from '../../../../providers/NotificationProvider';

export const MultiFactorAuthFields = React.memo(({ form, userId, onVerificationChange }) => {
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    // derive selectedMethod from form whenever it changes
    const selectedMethod = Form.useWatch('mfa_method', form);

    // RTKQ Hooks
    // Send MFA code to user's email
    const [sendMFAPhoneNumberCode] = useManageAccountSendMFAPhoneNumberCodeMutation();
    // Validate MFA code
    const [validateMFAPhoneNumberCode] = useManageAccountVerifyMFAPhoneNumberCodeMutation();

    const { notifyError, notifySuccess } = useNotificationContext();

    // Update these values whenever their inputs are updated
    const phoneNumber = Form.useWatch('phone_number', form);
    const mfaCode = Form.useWatch('mfa_code', form);

    // Reset verification whenever the phone number is edited
    useEffect(() => {
        if (isPhoneVerified || isCodeSent) {
            setIsPhoneVerified(false);
            setIsCodeSent(false);
            onVerificationChange?.(false);
        }
    }, [phoneNumber]);

    const handleSendCode = async () => {
        try {
            await form.validateFields(['phone_number']);
        } catch (error) {
            return;
        }

        try {
            await sendMFAPhoneNumberCode({ phoneNumber, userId });
            setIsCodeSent(true);
            notifySuccess({ message: 'We have sent you the verification code' });
        } catch (error) {
            notifyError({ message: 'Error sending verification code' });
            setIsCodeSent(false);
        }
    };

    const handleCheckCode = async () => {
        try {
            await form.validateFields(['mfa_code']);
        } catch (error) {
            return;
        }

        const verificationResult = await validateMFAPhoneNumberCode({ mfaCode, phoneNumber, userId });
        if (verificationResult?.data !== 'pass') {
            notifyError({ message: 'Incorrect verification code, please try again' });
            return;
        }
        notifySuccess({ message: 'Phone number verified successfully, you can now save your MFA settings' });
        setIsPhoneVerified(true);
        onVerificationChange?.(true);
        // Clear the MFA code input
        form.resetFields(['mfa_code']);
    };

    return (
        <Col span={24}>
            <Form.Item label="Select Two-Factor Authentication Method" name="mfa_method">
                <Radio.Group
                    options={[
                        { label: 'SMS', value: 'sms' },
                        { label: 'Email', value: 'email' },
                    ]}
                />
            </Form.Item>

            {selectedMethod === 'sms' && (
                <>
                    <Form.Item
                        name="phone_number"
                        label="Phone Number"
                        rules={[
                            { required: true, message: 'Please enter your phone number' },
                            {
                                pattern: /^[+][0-9]+$/,
                                message: 'Phone number must contain a + sign and digits (no dashes or spaces)',
                            },
                        ]}
                    >
                        <Input placeholder="+12345678900" />
                    </Form.Item>

                    {!isCodeSent && (
                        <Form.Item>
                            <Button type="dashed" disabled={!phoneNumber} onClick={handleSendCode}>
                                Send Verification Code
                            </Button>
                        </Form.Item>
                    )}

                    {isCodeSent && !isPhoneVerified && (
                        <>
                            <Form.Item
                                name="mfa_code"
                                label="Verification Code"
                                rules={[{ required: false, message: 'Please input the verification code' }]}
                            >
                                <Input placeholder="Enter the verification code" />
                            </Form.Item>
                            <Form.Item>
                                <Button type="dashed" disabled={!mfaCode} onClick={handleCheckCode}>
                                    Check Verification Code
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </>
            )}
        </Col>
    );
});
